import React, { CSSProperties, ReactNode, useState } from 'react'
import { ArrowForwardIos, Close } from '@mui/icons-material'
import cn from '../../lib/utils/cn'
import IconButton from './IconButton'
import { ClassNameValue } from 'tailwind-merge'

interface PopupProps {
	isOpen: boolean
	onClose?: () => void
	children: React.ReactNode
	title?: string | ReactNode
	icon?: ReactNode
	customControllers?: ReactNode | null
	className?: ClassNameValue
	style?: CSSProperties
	classNameTitle?: ClassNameValue
	withoutExpanding?: boolean
	relativePosition?: boolean
	expanded?: boolean
	onExpandedChange?: (expanded: boolean) => void
}

const Popup: React.FC<PopupProps> = ({
	isOpen,
	onClose,
	children,
	title = 'Title',
	icon,
	customControllers,
	className,
	style,
	classNameTitle,
	withoutExpanding = false,
	relativePosition = false,
	expanded,
	onExpandedChange,
}) => {
	const [isFull, setIsFull] = useState(true)
	const isExpanded = expanded ?? isFull

	const handleExpandToggle = () => {
		const nextExpanded = !isExpanded
		if (expanded === undefined) {
			setIsFull(nextExpanded)
		}
		onExpandedChange?.(nextExpanded)
	}

	if (!isOpen) return null

	const renderCloseButton = (isAbsolute?: boolean) => {
		return (
			<IconButton
				variant="transparent"
				onClick={onClose}
				icon={<Close className="w-5" />}
				className={cn('w-10 h-10', isAbsolute && 'absolute top-2 right-2 z-10')}
			/>
		)
	}

	return (
		<div
			style={style}
			className={cn(
				'bg-white dark:bg-background-dark p-3 border w-full sm:max-w-[408px] sm:min-w-[408px] border-stroke-dark dark:border-stroke-light-dark rounded-xl flex flex-col items-center default-shadow justify-center z-[1000] box-border',
				relativePosition ? 'relative' : 'fixed right-10 bottom-[70px]',
				className,
			)}
			onClick={(e) => e.stopPropagation()}
		>
			{onClose && withoutExpanding && renderCloseButton(true)}
			<div className="w-full rounded-lg relative flex items-center justify-between pb-1">
				<h3
					className={cn(
						'flex items-center gap-2 text-lg font-medium',
						classNameTitle,
					)}
				>
					<div className="sm:flex justify-center items-center hidden">
						{!!icon && icon}
					</div>
					{title}
				</h3>
				<div className="flex items-center justify-end gap-2">
					{customControllers}

					{!withoutExpanding && (
						<div className="flex items-center justify-end gap-1">
							<IconButton
								variant="transparent"
								onClick={handleExpandToggle}
								icon={
									<ArrowForwardIos
										className={cn(
											'w-5',
											isExpanded ? 'rotate-90' : '-rotate-90',
										)}
									/>
								}
								className={cn('w-10 h-10')}
							/>
							{!!onClose && renderCloseButton()}
						</div>
					)}
				</div>
			</div>
			{isExpanded && !!children && children}
		</div>
	)
}

export default Popup
